import { useEffect, useState } from 'react';
import { Users, Heart, MessageCircle, Building2, Calendar, Flag, FileCheck, Loader2, Database, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/adminApi';

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <div data-testid={testId} className="bg-[#1A1820] border border-white/10 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wider text-white/50">{label}</span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
          <Icon className="w-4.5 h-4.5" style={{ color }} />
        </div>
      </div>
      <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{(value ?? 0).toLocaleString()}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedingRealistic, setSeedingRealistic] = useState(false);
  const [wiping, setWiping] = useState(false);

  async function loadStats() {
    try {
      const data = await adminApi('/admin/dashboard');
      setStats(data.stats || data);
    } catch (err) {
      toast.error(err.message || 'Failed to load stats');
    }
  }

  useEffect(() => { loadStats(); }, []);

  async function handleSeed() {
    if (!window.confirm('Seed demo data? This will REPLACE all existing seed profiles/businesses/events/communities. User-submitted data and real users will NOT be touched.')) return;
    setSeeding(true);
    try {
      const res = await adminApi('/admin/seed-demo-data', { method: 'POST' });
      toast.success(`Seeded ${res.profiles} profiles · ${res.businesses} businesses · ${res.events} events · ${res.communities} communities`);
      loadStats();
    } catch (err) {
      toast.error(err.message || 'Seeding failed');
    } finally {
      setSeeding(false);
    }
  }

  async function handleSeedRealistic() {
    if (!window.confirm(
      'Seed 20 realistic profiles (10 dark-skinned women + 10 dark-skinned men) with AI-generated portraits?\n\n' +
      'Idempotent: re-running replaces the previous "realistic_20" batch so you never get duplicates.\n\n' +
      'These appear immediately in Discover for any user.'
    )) return;
    setSeedingRealistic(true);
    try {
      const res = await adminApi('/admin/seed-realistic-20', { method: 'POST' });
      toast.success(res.message || `Seeded ${res.count || 20} realistic profiles`);
      loadStats();
    } catch (err) {
      toast.error(err.message || 'Realistic seed failed');
    } finally {
      setSeedingRealistic(false);
    }
  }

  async function handleWipeSeedUsers() {
    if (!window.confirm(
      'WIPE SEED USER PROFILES?\n\n' +
      'This removes ALL fake/AI-generated profiles (anyone with is_seed=true OR @afroconnect.seed email) plus their likes/matches/messages.\n\n' +
      'Real user signups, admins, businesses, events, and communities will be KEPT.\n\n' +
      'Recommended before going live so users only interact with real people.'
    )) return;
    setWiping(true);
    try {
      const res = await adminApi('/admin/wipe-seed-data?users_only=true', { method: 'POST' });
      const d = res.deleted || {};
      toast.success(`Wiped ${d.users || 0} seed users · ${d.likes || 0} likes · ${d.matches || 0} matches · ${d.messages || 0} messages`);
      loadStats();
    } catch (err) {
      toast.error(err.message || 'Wipe failed');
    } finally {
      setWiping(false);
    }
  }

  if (!stats) {
    return <div className="flex items-center justify-center h-full p-12"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>;
  }

  const isEmpty = !stats.total_users && !stats.total_businesses && !stats.total_events;

  return (
    <div className="p-8" data-testid="admin-dashboard-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-serif font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Dashboard</h1>
          <p className="text-white/50 text-sm">Live snapshot — same numbers across web and mobile.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleWipeSeedUsers}
            disabled={wiping || seeding || seedingRealistic}
            data-testid="admin-wipe-seed-users-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1A1820] border border-[#E76F51]/40 hover:bg-[#E76F51]/10 hover:border-[#E76F51] disabled:opacity-60 text-[#E76F51] text-sm font-bold transition-colors"
            title="Remove all AI-generated seed profiles before going live"
          >
            {wiping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {wiping ? 'Wiping…' : 'Wipe seed users'}
          </button>
          <button
            onClick={handleSeedRealistic}
            disabled={seeding || wiping || seedingRealistic}
            data-testid="admin-seed-realistic-20-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1A1820] border border-[#2A9D8F]/40 hover:bg-[#2A9D8F]/10 hover:border-[#2A9D8F] disabled:opacity-60 text-[#2A9D8F] text-sm font-bold transition-colors"
            title="Seed 20 curated realistic profiles (10 women + 10 men, all dark-skinned African Canadians)"
          >
            {seedingRealistic ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {seedingRealistic ? 'Seeding…' : 'Seed 20 realistic'}
          </button>
          <button
            onClick={handleSeed}
            disabled={seeding || wiping || seedingRealistic}
            data-testid="admin-seed-demo-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-[#E07A5F] to-[#C8553D] hover:opacity-90 disabled:opacity-60 text-white text-sm font-bold shadow-lg shadow-[#C8553D]/20"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {seeding ? 'Seeding…' : 'Seed demo data'}
          </button>
        </div>
      </div>

      {isEmpty && (
        <div data-testid="admin-empty-state" className="mb-6 p-5 rounded-2xl border border-[#E07A5F]/40 bg-[#E07A5F]/10 text-sm text-white/80">
          <strong className="text-[#E07A5F]">Your database looks empty.</strong> Click <em>Seed demo data</em> to populate 45 profiles, 9 businesses, 12 events, and 5 communities so Discover and directories aren't blank.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={stats.total_users} icon={Users} color="#E07A5F" testId="stat-total-users" />
        <StatCard label="Active Users" value={stats.active_users} icon={Users} color="#2A9D8F" testId="stat-active-users" />
        <StatCard label="Premium" value={stats.premium_users} icon={Heart} color="#E9C46A" testId="stat-premium-users" />
        <StatCard label="Banned" value={stats.banned_users} icon={Flag} color="#E76F51" testId="stat-banned-users" />
        <StatCard label="Total Matches" value={stats.total_matches} icon={Heart} color="#C8553D" testId="stat-matches" />
        <StatCard label="Messages Sent" value={stats.total_messages} icon={MessageCircle} color="#457B9D" testId="stat-messages" />
        <StatCard label="Businesses" value={stats.total_businesses} icon={Building2} color="#9E3A26" testId="stat-businesses" />
        <StatCard label="Events" value={stats.total_events} icon={Calendar} color="#264653" testId="stat-events" />
        <StatCard label="Pending Submissions" value={stats.pending_submissions} icon={FileCheck} color="#E9C46A" testId="stat-pending-submissions" />
        <StatCard label="Pending Reports" value={stats.pending_reports} icon={Flag} color="#E76F51" testId="stat-pending-reports" />
      </div>
    </div>
  );
}
